import {
  createContext, useCallback, useContext, useEffect, useState,
  type ReactNode, type JSX,
} from 'react';
import type { ThemeMode } from './vloud-theme';

/**
 * Minimal theme provider — Vite/React-19 equivalent of agent-system's
 * next-themes-based setup. Stores the chosen mode in localStorage,
 * applies `[data-theme]` on <html> so CSS variables switch, and
 * exposes a `useTheme()` hook for components (the sidebar toggle).
 *
 * Default is dark (matches Agent Studio's defaultTheme="dark").
 * No SSR — this app is pure CSR.
 */

const STORAGE_KEY = 'vloud.admin.theme';

interface ThemeContextValue {
  theme: ThemeMode;
  setTheme: (t: ThemeMode) => void;
  toggle: () => void;
}

const Ctx = createContext<ThemeContextValue | null>(null);

function readStored(): ThemeMode {
  if (typeof localStorage === 'undefined') return 'dark';
  const raw = localStorage.getItem(STORAGE_KEY);
  return raw === 'light' ? 'light' : 'dark';
}

function applyToDocument(t: ThemeMode): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', t);
  document.documentElement.style.colorScheme = t;
}

export function ThemeProvider({ children }: { children: ReactNode }): JSX.Element {
  const [theme, setThemeState] = useState<ThemeMode>(readStored);

  useEffect(() => {
    applyToDocument(theme);
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  const setTheme = useCallback((t: ThemeMode) => setThemeState(t), []);
  const toggle   = useCallback(
    () => setThemeState((t) => (t === 'dark' ? 'light' : 'dark')),
    [],
  );

  return <Ctx.Provider value={{ theme, setTheme, toggle }}>{children}</Ctx.Provider>;
}

export function useTheme(): ThemeContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useTheme outside ThemeProvider');
  return v;
}
